import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button, Input } from '../components/ui';
import { Lock, Mail, Loader2 } from 'lucide-react';
import logoFull from '../assets/logo_full.png';
import logoMark from '../assets/logo_mark.png';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password.trim());
    setLoading(false);
    if (res.ok) {
      toast.success('Welcome back');
      navigate('/dashboard');
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden bg-[#0F172A]">
        <img
          src="https://images.unsplash.com/photo-1728726558882-1ae77b8a9a4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxzY2FyZiUyMGZhYnJpYyUyMHRleHR1cmUlMjBsaWdodHxlbnwwfHx8fDE3ODY1NTE5NjZ8MA&ixlib=rb-4.1.0&q=85"
          alt="Scarf fabric texture"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src={logoMark} alt="Khayyat Al-Babji logo" className="w-14 h-14 rounded-xl bg-white object-cover p-0.5" data-testid="login-logo" />
            <div>
              <p className="font-heading font-bold text-lg leading-tight">Khayyat Al-Babji Lil-Sayyidat</p>
              <p className="text-sm text-slate-300 font-arabic">خياط البابجي للسيدات</p>
            </div>
          </div>
          <div>
            <h1 className="font-heading font-extrabold text-4xl leading-tight tracking-tight">
              Weaving numbers<br />into growth.
            </h1>
            <p className="mt-4 text-slate-300 max-w-md">
              Manage fabric inventory, cutting & tailoring workflows, wholesale and retail billing, and financials — all in Kuwaiti Dinar.
            </p>
          </div>
          <p className="text-xs text-slate-400">© 2026 Khayyat Al-Babji Lil-Sayyidat · khayyatalbabji.com · Kuwait</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={logoFull} alt="Khayyat Al-Babji" className="w-44 object-contain" />
          </div>

          <h2 className="text-2xl font-heading font-extrabold tracking-tight text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Access your business management dashboard.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-[34px] text-slate-400" />
              <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@khayyatalbabji.com" className="pl-9" required data-testid="login-email" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-[34px] text-slate-400" />
              <Input label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="pl-9" required data-testid="login-password" />
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
